import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Facility {
  id: string;
  org_id: string;
  code: string;
  name: string;
  address: string | null;
  notes: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  dropbox_inbox_path: string | null;
  dropbox_folder_prefix: string | null;
}

export interface CreateFacilityInput {
  code: string;
  name: string;
  address?: string;
  notes?: string;
  sort_order?: number;
}

export interface UpdateFacilityInput {
  code?: string;
  name?: string;
  address?: string | null;
  notes?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

export function useFacilities() {
  return useQuery({
    queryKey: ["facilities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("facility")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw error;
      return data as Facility[];
    },
    staleTime: 300000, // Cache for 5 minutes - facilities rarely change
  });
}

/**
 * Facilities the Documents module has client-folder mapping configured for
 * (a non-null `dropbox_folder_prefix`). Single source of truth for "is this
 * facility Documents-enabled" -- everywhere Documents needs to loop over
 * GWH/RP/etc. should use this instead of re-deriving its own filter.
 */
export function useDocumentFacilities() {
  const { data: facilities, ...rest } = useFacilities();
  const documentFacilities = useMemo(
    () => (facilities ?? []).filter((f) => f.dropbox_folder_prefix),
    [facilities]
  );
  return { ...rest, data: documentFacilities };
}

export function useAllFacilities() {
  return useQuery({
    queryKey: ["facilities", "all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("facility")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw error;
      return data as Facility[];
    },
    staleTime: 300000,
  });
}

export function useCreateFacility() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateFacilityInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("facility")
        .insert([{
          code: input.code,
          name: input.name,
          address: input.address || null,
          notes: input.notes || null,
          sort_order: input.sort_order || 0,
          org_id: userData.org_id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as Facility;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facilities"] });
      toast({
        title: "Facility created",
        description: "The facility has been successfully created.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating facility",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateFacility() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateFacilityInput }) => {
      const { data, error } = await supabase
        .from("facility")
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Facility;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facilities"] });
      toast({
        title: "Facility updated",
        description: "The facility has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating facility",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteFacility() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("facility").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facilities"] });
      toast({
        title: "Facility deleted",
        description: "The facility has been successfully deleted.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting facility",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
